/**
 * Which ZKX deployment this build talks to — ONE switch for every target
 * (extension, mobile-web, native) and every backend (relayer/velcro/cena/
 * invictus, LibertySwap hosts, the gasless relayer contract, the integrator id).
 *
 * Set `ENV_MODE` in `extension/.env`: "dev" | "uat" | "prod".
 *
 * The key was called plain `ENV` until 2026-08-23, which collided with a name
 * generic enough that a CI job already used it: the deploy pipeline exported an
 * `ENV` holding the WHOLE dotenv file (it writes `.env` from it), dotenv refuses
 * to overwrite an already-set variable, and babel inlined that blob here. The
 * `find` below matched nothing, the fallback resolved to `prod`, and
 * app.dev.zkxwallet.com shipped with prod hosts and the prod $10 swap minimum
 * while every other key was correctly dev. `ENV_MODE` is specific enough not to
 * be squatted on; do not shorten it back.
 *
 * Deliberately NOT the same thing as `APP_ENV`. `APP_ENV`
 * (development/staging/production) is the BUILD KIND — minification, source
 * maps, Sentry environment, dev-only tooling — and every yarn script hardcodes
 * it (`web:mobile` is always development, `build:web:mobile` always production).
 * Folding the two together would make "a production build pointed at the dev
 * backend" (QA) and "a local dev server pointed at prod" (release verification)
 * unexpressible.
 *
 * It lives in the submodule rather than in the app's `common/config/env` because
 * two modules HERE need it too (`utils/networks.ts` for the ZKX API host and
 * `controllers/swapAndBridge` for the minimum swap size) and they cannot import
 * from the app. The app re-exports the derived flags from `common/config/env`,
 * so nothing outside these files reads `process.env` for the environment.
 *
 * `uat` is production in every respect — same hosts, same contracts, same
 * integrator — and exists so a release candidate can be labelled as one.
 *
 * Values are inlined at build time by babel's
 * `transform-inline-environment-variables`, so this whole module folds down to
 * a constant and the unused branches tree-shake away.
 */
export type ZkxEnv = 'dev' | 'uat' | 'prod'

export const ZKX_ENVS: ZkxEnv[] = ['dev', 'uat', 'prod']

/**
 * A missing or unrecognised `ENV_MODE` (a typo, or a CI runner that already
 * exports the same name for something else — see the header, that is not
 * hypothetical) resolves to `prod`, not `dev`: a build that talks to production
 * when it meant dev is an annoyance, the reverse ships dev contracts and dev
 * funds to users. `scripts/validateEnv.js` fails an extension release build
 * outright when `ENV_MODE="dev"` meets `APP_ENV=production`, so this fallback is
 * a floor, not a way to ship the wrong thing quietly. Note the failure is SILENT
 * on a dev build, which is exactly how the collision above went unnoticed.
 */
export const ZKX_ENV: ZkxEnv = ZKX_ENVS.find((env) => env === process.env.ENV_MODE) || 'prod'

/** True only on a build pointed at the dev backend. */
export const isZkxDevEnv = ZKX_ENV === 'dev'

/** True only on a production build (uat counts as "not prod" ONLY for the
 * relaxed swap minimum — every host/contract it talks to is still prod). */
export const isZkxProdEnv = ZKX_ENV === 'prod'

/**
 * Pick a value for the current environment from an explicit `dev | uat | prod`
 * table.
 *
 * Prefer this over `isZkxDevEnv ? a : b` for anything that is per-environment
 * CONFIG (a host, a contract address, a key). There are three environments, and
 * a boolean can only express two of them: every `? :` silently folds `uat` into
 * whichever branch the author happened to pick, so the day `uat` has to differ
 * from `prod` the call site has to be rediscovered and rewritten. Spelling all
 * three out means `uat: PROD_VALUE` is a recorded decision rather than an
 * accident, and TypeScript fails the build when a new environment is added and
 * a table is not updated.
 *
 * The booleans above stay for the cases that really are binary — "is this the
 * dev backend at all" style gates in the UI, not address/host tables.
 *
 * Cost, versus the `isZkxDevEnv ? a : b` ternaries this replaced: babel still
 * inlines `ENV_MODE`, but a table is an object literal and the lookup happens at
 * runtime, so the OTHER environments' values stay in the bundle instead of being
 * folded away. That is fine for what belongs here — public addresses and hosts,
 * a few hundred bytes — and it is the reason this helper must never be handed a
 * secret or anything else that should not ship to every environment.
 */
export const byZkxEnv = <T>(table: Record<ZkxEnv, T>): T => table[ZKX_ENV]
